// BRN-2123 — composes one `OperatorViewportSnapshot` (api-contract.md §2) from what a page RENDERED.
//
// SECURITY REVIEW E2 (ELEVATION OF PRIVILEGE, HIGH) IS WHY THIS FUNCTION HAS THE SIGNATURE IT HAS.
// The snapshot must contain nothing the viewer could not already see, and brain-agents cannot verify
// that — it has no way to know what the viewer's page rendered. So the guarantee has to be
// structural on this side: `composeSnapshotContent` takes a `ViewportViewModel` and NOTHING else. It
// cannot reach a store, a cache, a fetcher or a router. A store routinely holds more than the page
// shows — a prefetched list, a fuller entity than the view renders, a sibling route's slice — and
// composing from one would put unrendered data into a channel a model may quote back. Callers
// project what they rendered; there is no other input for them to pass by mistake.
//
// Everything here is bounded and normalized at the source rather than trusted to the engine's
// rejection. The engine still enforces its own bounds and discloses `viewport_context_too_large` —
// that is the authority — but a host that habitually posts oversized snapshots is one whose
// grounding silently stops working, so the bounds are applied where the data is composed.

import type { ViewportSurface } from './surface-registry-types';

/** api-contract.md §2 cardinality and length bounds. Named because each is a contract number, not a
 *  taste: changing one here without changing the contract makes the engine drop what this sends. */
const MAX_ENTITIES = 24;
const MAX_SELECTION = 24;
const MAX_FILTER_KEYS = 16;
const MAX_VISIBLE = 8;
const MAX_HIGHLIGHTS = 12;
const MAX_COLUMNS = 24;
const MAX_TYPE_CHARS = 64;
const MAX_ID_CHARS = 128;
const MAX_LABEL_CHARS = 120;
const MAX_VALUE_CHARS = 120;
const MAX_HIGHLIGHT_LABEL_CHARS = 64;
const MAX_COLUMN_CHARS = 64;
const MAX_ROUTE_CHARS = 512;
const MAX_SORT_CHARS = 120;
const MAX_SCOPE_CHARS = 120;
/** The whole serialized envelope, api-contract.md §2. The engine disposes anything larger as
 *  `viewport_context_too_large`; sections are dropped here, least-essential first, so a page with a
 *  very wide render still grounds the turn on its surface and entities instead of on nothing. */
const MAX_ENVELOPE_BYTES = 8_192;
/** The envelope version this host speaks. */
const SNAPSHOT_SCHEMA = 1;

/** Control characters, written as escapes rather than as literal bytes so this class is readable
 *  in a diff and cannot be mangled by a copy. Newlines and tabs are covered by it. */
// eslint-disable-next-line no-control-regex -- collapsing control characters is exactly the point
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]+/g;

export type ViewportEntityRole = "primary" | "secondary";

export interface ViewportEntityRef {
  readonly type: string;
  readonly id: string;
  readonly label?: string;
  readonly role?: ViewportEntityRole;
}

export interface ViewportSelectionRef {
  readonly type: string;
  readonly id: string;
}

export interface ViewportTimeframe {
  readonly from: string;
  readonly to: string;
}

export type ViewportFilterValue = string | number | boolean | ViewportTimeframe;

export type ViewportVisibleKind = "table" | "chart" | "list" | "form" | "map" | "detail" | "other";

export interface ViewportVisible {
  readonly kind: ViewportVisibleKind;
  readonly id: string;
  readonly label?: string;
  readonly columns?: readonly string[];
  readonly rowCount?: number;
  readonly sort?: string;
  readonly scope?: string;
}

export interface ViewportHighlight {
  readonly label: string;
  readonly value: string;
  /** `"<type>:<id>"` — which entity the highlight is about. */
  readonly of?: string;
}

/** What a page publishes about ITS OWN RENDER. Every field optional: a declared page that renders a
 *  heading and nothing structured still grounds the turn on its surface identity alone. */
export interface ViewportViewModel {
  readonly entities?: readonly ViewportEntityRef[];
  readonly selection?: readonly ViewportSelectionRef[];
  readonly filters?: Readonly<Record<string, ViewportFilterValue>>;
  readonly visible?: readonly ViewportVisible[];
  readonly highlights?: readonly ViewportHighlight[];
}

export interface SnapshotSurface {
  readonly id: string;
  readonly label: string;
  readonly route?: string;
}

/** The content-bearing half of the envelope: everything except `revision` and `capturedAt`, which
 *  are stamped at send time and must never take part in change detection. */
export interface OperatorViewportSnapshotContent {
  readonly schema: number;
  readonly surface: SnapshotSurface;
  readonly entities?: ViewportEntityRef[];
  readonly selection?: ViewportSelectionRef[];
  readonly filters?: Record<string, ViewportFilterValue>;
  readonly visible?: ViewportVisible[];
  readonly highlights?: ViewportHighlight[];
}

export interface OperatorViewportSnapshot extends OperatorViewportSnapshotContent {
  readonly revision: number;
  readonly capturedAt: string;
}

export function composeSnapshotContent(
  surface: ViewportSurface,
  view: ViewportViewModel,
  route?: string,
): OperatorViewportSnapshotContent {
  const content: Record<string, unknown> = {
    schema: SNAPSHOT_SCHEMA,
    surface: {
      id: surface.id,
      label: normalize(surface.label, MAX_LABEL_CHARS),
      ...(route ? { route: normalize(route, MAX_ROUTE_CHARS) } : {}),
    },
  };
  assign(content, "entities", normalizeEntities(view.entities));
  assign(content, "selection", normalizeSelection(view.selection));
  assign(content, "filters", normalizeFilters(view.filters));
  assign(content, "visible", normalizeVisible(view.visible));
  // I4: a surface whose declaration forbids highlights emits no `highlights` key at all — not an
  // empty array, which would still be a claim. The engine independently disposes a snapshot that
  // names such a surface and carries highlights anyway; this is the host end of the same rule.
  if (surface.highlightsAllowed) {
    assign(content, "highlights", normalizeHighlights(view.highlights));
  }
  return fitEnvelope(content as unknown as OperatorViewportSnapshotContent);
}

/** The change-detection identity: stable, and blind to `revision`/`capturedAt` by construction
 *  because they are not part of the content. Two renders of the same view produce the same string,
 *  so a re-render is never a new revision. */
export function snapshotIdentity(content: OperatorViewportSnapshotContent): string {
  return stableStringify(content);
}

export function stampSnapshot(
  content: OperatorViewportSnapshotContent,
  revision: number,
  capturedAt: string,
): OperatorViewportSnapshot {
  return { ...content, revision, capturedAt };
}

function assign(target: Record<string, unknown>, key: string, value: unknown[] | Record<string, unknown> | undefined): void {
  if (value === undefined) {
    return;
  }
  if (Array.isArray(value) ? value.length > 0 : Object.keys(value).length > 0) {
    target[key] = value;
  }
}

/** Control characters, newlines and tabs collapse to single spaces, then the string is capped. No
 *  field can therefore smuggle line structure into the fenced prompt block downstream. */
function normalize(value: string, max: number): string {
  return value.replace(CONTROL_CHARACTERS, " ").replace(/\s+/g, " ").trim().slice(0, max);
}

function normalizeEntities(entities: readonly ViewportEntityRef[] | undefined): ViewportEntityRef[] | undefined {
  return entities?.slice(0, MAX_ENTITIES).map((entity) => ({
    type: normalize(entity.type, MAX_TYPE_CHARS),
    id: normalize(entity.id, MAX_ID_CHARS),
    ...(entity.label === undefined ? {} : { label: normalize(entity.label, MAX_LABEL_CHARS) }),
    ...(entity.role === undefined ? {} : { role: entity.role }),
  }));
}

function normalizeSelection(selection: readonly ViewportSelectionRef[] | undefined): ViewportSelectionRef[] | undefined {
  return selection?.slice(0, MAX_SELECTION).map((reference) => ({
    type: normalize(reference.type, MAX_TYPE_CHARS),
    id: normalize(reference.id, MAX_ID_CHARS),
  }));
}

function normalizeFilters(
  filters: Readonly<Record<string, ViewportFilterValue>> | undefined,
): Record<string, ViewportFilterValue> | undefined {
  if (filters === undefined) {
    return undefined;
  }
  const entries = Object.entries(filters)
    .slice(0, MAX_FILTER_KEYS)
    .map(([key, value]) => [normalize(key, MAX_TYPE_CHARS), normalizeFilterValue(value)] as const);
  return Object.fromEntries(entries);
}

function normalizeFilterValue(value: ViewportFilterValue): ViewportFilterValue {
  if (typeof value === "string") {
    return normalize(value, MAX_VALUE_CHARS);
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  return { from: normalize(value.from, MAX_VALUE_CHARS), to: normalize(value.to, MAX_VALUE_CHARS) };
}

function normalizeVisible(visible: readonly ViewportVisible[] | undefined): ViewportVisible[] | undefined {
  return visible?.slice(0, MAX_VISIBLE).map((item) => ({
    kind: item.kind,
    id: normalize(item.id, MAX_ID_CHARS),
    ...(item.label === undefined ? {} : { label: normalize(item.label, MAX_LABEL_CHARS) }),
    ...(item.columns === undefined
      ? {}
      : { columns: item.columns.slice(0, MAX_COLUMNS).map((column) => normalize(column, MAX_COLUMN_CHARS)) }),
    ...(item.rowCount === undefined ? {} : { rowCount: item.rowCount }),
    ...(item.sort === undefined ? {} : { sort: normalize(item.sort, MAX_SORT_CHARS) }),
    ...(item.scope === undefined ? {} : { scope: normalize(item.scope, MAX_SCOPE_CHARS) }),
  }));
}

function normalizeHighlights(highlights: readonly ViewportHighlight[] | undefined): ViewportHighlight[] | undefined {
  return highlights?.slice(0, MAX_HIGHLIGHTS).map((highlight) => ({
    label: normalize(highlight.label, MAX_HIGHLIGHT_LABEL_CHARS),
    value: normalize(highlight.value, MAX_VALUE_CHARS),
    ...(highlight.of === undefined ? {} : { of: normalize(highlight.of, MAX_ID_CHARS + MAX_TYPE_CHARS + 1) }),
  }));
}

/** Drops whole sections, least essential first, until the envelope fits the contract's byte bound.
 *  `surface` and `schema` are never droppable — a snapshot that cannot name its surface is not a
 *  smaller snapshot, it is a malformed one. */
function fitEnvelope(content: OperatorViewportSnapshotContent): OperatorViewportSnapshotContent {
  const droppable: (keyof OperatorViewportSnapshotContent)[] = ["highlights", "visible", "filters", "selection", "entities"];
  const working: Record<string, unknown> = { ...content };
  for (const key of droppable) {
    if (byteLength(working) <= MAX_ENVELOPE_BYTES) {
      break;
    }
    delete working[key];
  }
  return working as unknown as OperatorViewportSnapshotContent;
}

function byteLength(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value) ?? "").length;
}

/** Key-ordered JSON, so two structurally equal contents serialize identically regardless of the
 *  order the page happened to build its object in. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value) ?? "null";
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, nested]) => nested !== undefined)
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0));
  return `{${entries.map(([key, nested]) => `${JSON.stringify(key)}:${stableStringify(nested)}`).join(",")}}`;
}
